import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import {
  UccxAgent,
  UccxAgentRef,
  UccxAgentSkill,
  UccxFieldConfig,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import { Observable, Subscription, throwError } from 'rxjs';
import { HtmlInputType, InputSize, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../../forms/fields/checkbox/ziro-checkbox.component';
import { TranslateService } from '@ngx-translate/core';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { ZiroNavsetConfig, ZiroNavsetItemConfig } from '../../../../shared/ziro-navset/ziro-navset.component';
import { UccxAgentResource } from '../../../shared/resources/uccx-agent.resource';
import { catchError, tap } from 'rxjs/operators';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../../shared/services/toast.service';
import { cloneDeep } from 'lodash';
import { UserDetailUiContext } from '../../../shared/contexts/user-detail-ui.context';
import { Global360ViewContext } from '../../../../shared/contexts/global-360-view.context';

export interface UccxAgentFormData extends UccxAgent {
  uccxNavset: any;
}

@Component({
  selector: 'ziro-uccx-agent-form',
  templateUrl: './uccx-agent-form.component.html',
  standalone: false,
})
export class UccxAgentFormComponent
  extends ZiroFormAbstractDirective<UccxAgent, UccxAgentFormData>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private translateService = inject(TranslateService);
  private bottomNavService = inject(BottomNavService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private uccxAgentResource = inject(UccxAgentResource);
  private toastService = inject(ToastService);
  private userDetailUiContext = inject(UserDetailUiContext);
  private global360ViewContext = inject(Global360ViewContext);

  ziroIcons = ZiroIcons;
  readonly uccxAgentRef = input<UccxAgentRef>();
  readonly uccxServer = input<string>();
  readonly fieldConfig = input<UccxFieldConfig>();
  readonly resourceGroups = input<string[]>();

  isLoading = false;
  isSubmitted = false;
  allSkills: string[] = [];

  formConfig: ZiroFormConfig;

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._initUccxData();
    this._initFormConfig();
    this._initBottomNav();

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<UccxAgent>) => {
      if (data.valid === ZiroFormsValidationState.VALID && this.isFormSubmitted) {
        this.bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: !this.isFormValid(),
          }),
        );
      }
    });

    this._subscriptions.add(formUpdateSub);

    Object.keys(this.fieldChannels).forEach((key) => {
      this.fieldChannels[key].validateSource.next();
    });

    const validateAndSubmitSub = this._validateAndSubmitSource.subscribe(() => {
      this.isSubmitted = true;
    });
    this._subscriptions.add(validateAndSubmitSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
    this.bottomNavService.dispatch(new BottomNavClearButtonsList());
  }

  toFormData = (entity: UccxAgent): UccxAgentFormData => {
    const entityClone = cloneDeep(entity);

    const skills: any = {};

    entityClone.skills.forEach((skill) => {
      const { name, competenceLevel } = skill;
      const formattedSkill: any = {};
      formattedSkill['competenceLevel'] = competenceLevel;
      skills[name] = [formattedSkill];
    });

    return {
      ...entityClone,
      uccxNavset: {
        ...skills,
      },
    };
  };

  toEntity = (formData: UccxAgentFormData): UccxAgent => {
    const formDataClone = cloneDeep(formData);
    const skills: UccxAgentSkill[] = [];

    if (formDataClone.uccxNavset) {
      Object.keys(formDataClone['uccxNavset']).forEach((key) => {
        skills.push({ name: key, competenceLevel: parseInt(formDataClone['uccxNavset'][key][0].competenceLevel) });
      });
    }

    delete formDataClone['uccxNavset'];

    return {
      ...formDataClone,
      skills: skills,
    };
  };

  protected submit(): Observable<UccxAgentRef> {
    this.bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
      }),
    );

    this._setPending(true);
    const apiRequest = this.uccxAgentResource.put(this.entity, this.uccxAgentRef().serverId, this.entity.id);

    return apiRequest.pipe(
      tap((response: UccxAgentRef) => {
        const global360 = cloneDeep(this.userDetailUiContext.getGlobal360View());
        const agentIndex = global360.uccxAgents.findIndex((agent) => agent.serverId === response.serverId);
        global360.uccxAgents[agentIndex] = response;
        this.global360ViewContext._stateSource.next(global360);
        this.toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.SAVE_SUCCESS,
          'tkey;shared.toast.save.success.title',
          'tkey;uccxagent.toast.success',
        );
        this.router.navigateByUrl(`/user/${encodeURIComponent(this.route.snapshot.params.username)}`);
      }),
      catchError((error) => {
        this.isSubmitting = false;
        this._validateAndSubmitSource.next(false);
        this._setPending(false);
        return throwError(() => error);
      }),
    );
  }

  private _initUccxData(): void {
    this.allSkills = this.fieldConfig().skills.sort();
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        resourceGroup: {
          label: 'tkey;uccxagent.details.resource_group.label',
          dataAutomation: 'uccx-resource-group',
          componentConfig: new ZiroSelectConfig({ options: this.resourceGroups() }),
          hidden: () => !this.fieldConfig().resourceGroup.show,
          required: () => this.fieldConfig().resourceGroup.required,
          defaultValue: () => this.fieldConfig().resourceGroup.defaultValue,
        },
        team: {
          label: 'tkey;uccxagent.details.team.label',
          dataAutomation: 'uccx-team',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().team.possibleOptions }),
          hidden: () => !this.fieldConfig().team.show,
          required: () => this.fieldConfig().team.required,
          defaultValue: () => this.fieldConfig().team.defaultValue,
        },
        alias: {
          label: 'tkey;uccxagent.details.alias.label',
          dataAutomation: 'uccx-alias',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          hidden: () => !this.fieldConfig().alias.show,
          required: () => this.fieldConfig().alias.required,
          defaultValue: () => this.fieldConfig().alias.defaultValue,
          validation: [
            {
              validator: (val) =>
                /^([a-z0-9]{0,2}|[a-z0-9][a-z0-9_.-]+[a-z0-9])$/i.test(val)
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID,
              message: 'tkey;validators.global.error.pattern',
            },
            {
              validator: (val: string) =>
                val.length <= 31 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: this.translateService.instant('tkey;validators.global.error.maxlength', { maxlength: 31 }),
            },
          ],
        },
        automaticAvailable: {
          label: 'tkey;uccxagent.details.automatic_available.label',
          dataAutomation: 'uccx-automatic-available',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
          hidden: () => !this.fieldConfig().automaticAvailable.show,
        },
        uccxNavset: {
          componentConfig: new ZiroNavsetConfig({
            dropdownLabel: 'tkey;uccxagent.details.skills.add_skill.text',
            dropdownTooltip: 'tkey;uccxagent.details.skills.no_more_skills.text',
            emptyMessage: 'tkey;uccxagent.details.skills.none.text',
            navsetItemsConfig: this._getSkillsConfig(),
            allowDuplicates: false,
          }),
        },
      },
    } as ZiroFormConfig;
  }

  private _getSkillsConfig = (): ZiroNavsetItemConfig => {
    const skillNavsetFields: ZiroNavsetItemConfig = {};
    this.allSkills.forEach((skill: string) => {
      skillNavsetFields[skill] = {
        label: skill,
        badgeFieldId: 'competenceLevel',
        fields: [
          {
            label: 'tkey;uccxagent.details.skills.competence_level.label',
            dataAutomation: 'competenceLevel',
            required: true,
            componentConfig: new ZiroTextConfig({
              htmlInputType: HtmlInputType.NUMBER,
              inputSize: InputSize.SM,
              min: 1,
              max: 10,
            }),
            validation: [
              {
                validator: (val: string) => {
                  return Number(val) >= 1 && Number(val) <= 10
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                },
                message: 'tkey;uccxagent.details.skills.competence_level.invalid',
              },
            ],
          },
        ],
      };
    });

    return {
      ...skillNavsetFields,
    };
  };

  private _initBottomNav: () => void = () => {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'agentCancel',
          dataAutomation: 'agent-cancel',
          label: 'tkey;global.button.cancel.text',
          buttonClass: ButtonStyles.DEFAULT,
          cb: () => this._goToUserHome(),
        },
        {
          id: 'agentSave',
          dataAutomation: 'agent-save',
          label: 'tkey;global.button.save.text',
          icon: this.ziroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subscriptions.add(formSubmittedSub);
  };

  private _goToUserHome(): void {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  private _setPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'agentSave',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'agentCancel',
        state: {
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }
}
